import styled, { css, keyframes } from 'styled-components';
import Link from 'next/link';
import {
  Button as AntdButton,
  Typography as AntdTypography,
  Avatar as AntdAvatar,
} from 'antd';
import { Text as OriginalText } from '../../common/atoms/Typography';

const pulseGreen = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(81, 213, 19, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(81, 213, 19, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(81, 213, 19, 0);
  }
`;

export const LoadingIconWrapper = styled.div<{ lookingForVetter?: boolean }>`
  margin-left: ${({ lookingForVetter }) =>
    lookingForVetter ? '8px' : '-48px'};
  position: relative;
  display: flex;
`;

export const IconWithPhotoWrapper = styled.div`
  margin-left: 12px;
  margin-right: 22px;
  position: relative;

  & svg {
    position: relative;
    box-shadow: 0 0 0 0 rgba(81, 213, 19, 1);
    animation: ${pulseGreen} 2s infinite;
    top: 22px;
    left: 26px;
  }
`;

export const Avatar = styled(AntdAvatar)`
  width: 48px;
  height: 48px;
  position: absolute;
  left: 0;
  top: -12px;
`;

export const Button = styled(AntdButton)<{
  $submitting?: boolean;
  $iconSize: 'medium' | 'large';
}>`
  position: relative;
  border: none;
  display: flex;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ $submitting, theme }) =>
    $submitting
      ? 'linear-gradient(277.66deg, rgba(255, 255, 255, 0.2) 38.81%, rgba(255, 255, 255, 0) 71.73%), rgba(0, 95, 249, 0.97)'
      : theme.colors.blue60};
  height: min-content;
  min-height: 72px;
  min-width: 335px;
  box-shadow: 12px 24px 48px -12px rgba(0, 95, 249, 0.25);

  justify-content: ${({ $submitting }) => ($submitting ? 'left' : 'center')};
  align-items: center;
  text-align: left;

  font-weight: 600;
  font-size: ${({ $submitting }) => ($submitting ? '15px' : '18px')};

  &.ant-btn-primary:hover {
    background: ${({ theme }) => theme.colors.blue60};
    color: ${({ theme }) => theme.colors.white};
    filter: brightness(1.1);
  }

  &.ant-btn > span {
    align-self: center;
    width: 100%;
  }

  &.ant-btn-primary:focus,
  :active {
    background: #1840da;
    color: ${({ theme }) => theme.colors.white};
  }

  &.ant-btn-primary[disabled] {
    box-shadow: 12px 24px 48px -12px rgba(0, 95, 249, 0.25);
    background: ${({ theme }) => theme.colors.grey30};
    color: ${({ theme }) => theme.colors.white};
    && svg {
      opacity: 0.7;
      background: ${({ theme }) => theme.colors.grey40};
    }
  }

  & svg {
    color: ${({ color, theme }) => (color ? color : theme.colors.white)};
    font-size: 18px;
    ${({ $iconSize }) => {
      switch ($iconSize) {
        case 'large':
          return css`
            width: 48px;
            height: 48px;
          `;
        default:
          return css`
            width: 36px;
            height: 36px;
          `;
      }
    }};

    position: relative;
    background: #51d513;
    border-radius: 50%;
    padding: 11px;
    font-size: 30px;
  }

  &.ant-btn-primary[disabled] svg {
    background: ${({ theme }) => theme.colors.grey30};
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const InnerWrap = styled.div`
  white-space: pre-wrap;
  margin: 23px 4px 22px 18px;
  max-width: 292px;

  @media (max-width: 576px) {
    margin-left: 18px;
    margin-right: 12px;
  }
`;

export const Text = styled(OriginalText)`
  color: ${({ theme }) => theme.colors.grey40};
`;

export const Typography = styled(AntdTypography)`
  text-align: center;
  margin-top: 12px;
`;

export const ExplanationLink = styled(Link)`
  &,
  &.ant-typography,
  .ant-typography &,
  &.ant-typography:active,
  .ant-typography &:active,
  &.ant-typography:hover,
  .ant-typography &:hover {
    color: ${({ theme }) => theme.colors.grey40};
    text-decoration: underline;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(52deg);
  }
  100% {
    transform: rotate(412deg);
  }
`;

export const RotatingBorder = styled.div`
  position: absolute;
  border-top: 30px solid ${({ theme }) => theme.colors.white};
  width: 54px;
  height: 54px;
  border-radius: 50%;
  top: 0;
  left: 0;
  transform: rotate(52deg);

  animation: ${spin} 2s linear infinite;
  z-index: 0;
  margin-left: -3px;
  margin-top: -3px;
`;
